import {CaptchaCtrl} from "./CaptchaCtrl";

/*
 * Le diagramme d'état du composant Captcha:
 * - INITIALIZING: état initial, le composant récupère la liste des indications et des images.
 *   Visuellement, le composant affiche un loading.
 * - COMPLETING: le composant affiche une liste d'indication et une liste d'image sélectionnable,
 *   l'utilisateur renseigne les images en fonction des indications.
 * - READY_TO_SUBMIT:  toutes les indications ont été renseignées par l'utilisateur, le bouton "Continuer" devient actif
 * - SUBMITTING: les réponses ont été soumises, en attente de validation
 * - VALIDATED: le captcha a bien été validé, toutes les réponses sont bonnes
 * - GENERIC_ERROR: le composant n'a pas pu récupérer la liste d'indications et les images.
 */
const CaptchaState = {
    INITIALIZING: "initializing",
    COMPLETING: "completing",
    READY_TO_SUBMIT: "ready_to_submit",
    SUBMITTING: "submitting",
    VALIDATED: "validated",
    GENERIC_ERROR: "generic_error",
}

Vue.component("Captcha", {
    props: {
        captchaApiBaseUrl: {
            type: String,
            required: true,
        },
    },
    data: function () {
        return {
            state: CaptchaState.INITIALIZING,
            captcha: null,  // indications et images du captcha
            hasError: false, // true si un captcha a éte soumis en erreur, false sinon
            selection: []   // sélection de l'utilisateur
        }
    },
    computed: {
        isLoading() {
            return this.state === CaptchaState.INITIALIZING || this.state === CaptchaState.SUBMITTING;
        },
        isInputDisplayed() {
            return this.state === CaptchaState.COMPLETING ||
                this.state === CaptchaState.READY_TO_SUBMIT;
        },
        isReadyToSubmit() {
            return this.state === CaptchaState.READY_TO_SUBMIT;
        },
        isGenericError() {
            return this.state === CaptchaState.GENERIC_ERROR;
        },
        currentIndex() {
            return this.selection.length;
        }
    },
    created() {
        this.captchaCtrl = new CaptchaCtrl({captchaApiBaseUrl: this.captchaApiBaseUrl});
        this.loadCaptcha();
    },
    methods: {
        /**
         * Récupère les indications et les url des images.
         */
        loadCaptcha() {
            this.state = CaptchaState.INITIALIZING;
            this.captchaCtrl.getCaptcha()
                .then((response) => {
                    this.captcha = response;
                    this.selection = [];
                    this.state = CaptchaState.COMPLETING;
                })
                .catch(() => this.state = CaptchaState.GENERIC_ERROR);
        },
        /**
         * Réinitialise la sélection de l'utilisateur.
         */
        resetSelection() {
            if (this.state === CaptchaState.COMPLETING || this.state === CaptchaState.READY_TO_SUBMIT) {
                this.selection = [];
                this.state = CaptchaState.COMPLETING;
            }
        },
        /**
         * Handler de l'événement click sur une image
         * On supprime le message d'erreur affiché s'il y en avait un
         * @param index index de l'image sur laquelle a
         */
        onImageButtonClick(index) {
            this.hasError = false;
            if (this.state === CaptchaState.READY_TO_SUBMIT) {
                return;
            }
            this.selection.splice(this.selection.length, 0, index);
            if (this.selection.length === this.captcha.indications.length) {
                this.state = CaptchaState.READY_TO_SUBMIT;
            } else {
                this.state = CaptchaState.COMPLETING;
            }
        },
        /**
         * Handler de l'événement click sur le bouton "suppression de la dernière sélection"
         */
        onDeleteLastButtonClick() {
            if (this.currentIndex <= 0) {
                return;
            }
            this.state = CaptchaState.COMPLETING;
            this.selection.splice(this.selection.length-1, 1);
        },
        onContinueButtonClick() {
            const data = {
                values: this.selection.map(index => {
                        return this.captcha.images[index].value
                    }
                )
            }
            $.ajax({
                method: "POST",
                url: this.captchaApiBaseUrl,
                dataType: "json",
                contentType: "application/json",
                data: JSON.stringify(data),
            }).done((response) => {
                if (response.result) {
                    this.$emit('captcha-valid');
                } else {
                    this.hasError = true;
                    this.captcha = response.captcha;
                    this.selection = [];
                    this.state = CaptchaState.COMPLETING;
                }
            }).fail((xhr) => {
                if (xhr.status === 403) {
                    this.loadCaptcha();
                } else {
                    this.state = CaptchaState.GENERIC_ERROR;
                    this.$emit('captcha-error');
                }
            });

        }
    },
    template: `
<div>

    <template v-if="isLoading">
        <div class="captcha-initializing d-flex justify-content-center m-1">
            <ob1-progress-indicators :size="50" type="undetermined" class="my-auto"/>
        </div>
    </template>
    <template v-else-if="isInputDisplayed">
        <div>
            <p class="text-mw">Cliquez sur les photos dans l’ordre indiqué pour confirmer que vous n’êtes pas un robot.</p>
        </div>
        <div v-if="hasError">
            <div class="alert-container alert-warning" role="alert">
                <p class="alert">
                    <span class="alert-icon" aria-hidden="true"></span>
                    <span class="ob1-alert-title">Saisie incorrecte</span>
                </p>
                <div class="alert-content">
                    <p class="alert-text text-mw">Vous n'avez pas cliqué sur les photos dans l'ordre demandé. Nous vous invitons à recommencer.</p>
                </div>
            </div>
        </div>
        <div class="captcha-container container-fluid">
            <div class="row">
                <div class="captcha-indications col-12 col-md-6">
                    <ul class="captcha-indications-list">
                        <li
                            v-for="(indication, index) in captcha.indications"
                            class="captcha-indications-list-item"
                            :class="index > currentIndex ? 'd-none d-md-flex' : null"
                        >
                            <template v-if="index < currentIndex">
                                <div class="captcha-indications-list-item-indication-done">
                                    <p class="captcha-indications-list-item-indication-index">{{ index + 1 }}.</p>
                                    <p class="captcha-indications-list-item-indication-label">{{ indication }}</p>
                                </div>
                                <div class="captcha-indications-list-item-selected">
                                    <img
                                        :src="captcha.images[selection[index]].url"
                                        class="captcha-indications-list-item-selected-image"
                                        alt="captcha sélectionnée"
                                    >
                                </div>
                                <div class="captcha-indications-list-item-delete">
                                    <button v-if="index === currentIndex - 1"
                                            class="captcha-indications-list-item-delete-button"
                                            @click="onDeleteLastButtonClick"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 1024 1024">
                                            <path d="M165.676 3.122c-6.002 1.385-14.771 5.078-19.542 8.002-10.463 6.463-125.56
                                        120.944-133.717 132.948-10.924 16.31-15.079 38.777-10.618 57.703 5.539 23.85-1.077
                                        16.465 152.182 170.031l140.333 140.333-140.333 140.487c-139.564 139.564-140.333
                                        140.333-145.872 152.182-6.616 14.465-8.463 23.081-7.694 37.699 0.616 14.926 5.386
                                        28.775 14.002 41.238 5.078 7.232 123.407 124.33 129.87 128.485 16.926 10.924 40.93
                                        14.618 60.164 9.386 21.696-5.847 18.312-2.769 167.876-152.182l139.717-139.564 140.333
                                        140.333c135.256 135.101 140.948 140.487 151.719 145.719 25.389 12.002 55.703 10.31
                                        76.476-4.463 9.54-6.771 124.022-120.791 130.177-129.562 11.387-16.465 16.157-41.085
                                        11.387-59.858-6.002-24.004 0.769-16.465-152.027-169.569l-140.333-140.333 140.178-140.487c99.402-99.402
                                        141.411-142.487 144.333-147.872 14.157-26.005 12.618-57.549-3.847-81.706-4.769-7.078-121.407-123.868-128.331-128.638-13.695-9.232-30.159-13.849-46.623-12.926-14.618
                                        0.616-26.005 4.155-37.238 11.079-5.539 3.539-56.78 53.703-147.258 144.025l-138.948
                                        138.795-140.333-140.333c-153.413-153.104-146.18-146.641-169.877-152.182-12.31-2.769-21.696-2.461-36.16 1.231z"
                                                  fill="#000"></path>
                                        </svg>
                                    </button>
                                </div>
                            </template>
                            <template v-else-if="index === currentIndex">
                                <div class="captcha-indications-list-item-indication-current">
                                    <p class="captcha-indications-list-item-indication-index">{{ index + 1 }}.</p>
                                    <p class="captcha-indications-list-item-indication-label">{{ indication }}</p>
                                </div>
                                <div class="captcha-indications-list-item-selection">
                                    <p class="captcha-indications-list-item-selection-label">?</p>
                                </div>
                            </template>
                            <template v-else>
                                <div class="captcha-indications-list-item-indication-todo">
                                    <p class="captcha-indications-list-item-indication-index">{{ index + 1 }}.</p>
                                    <p class="captcha-indications-list-item-indication-label">{{ indication }}</p>
                                </div>
                                <div class="captcha-indications-list-item-selection"></div>
                            </template>
                        </li>
                    </ul>
                </div>
                <div class="captcha-images col-12 col-md-6 pt-1 pt-md-0">
                    <ul class="captcha-images-list row">
                        <li v-for="(image, index) in captcha.images" class="col-4 captcha-images-list-item">
                            <button
                                class="captcha-images-list-item-button"
                                @click="onImageButtonClick(index)"
                            >
                                <img
                                    :src="image.url"
                                    class="captcha-images-list-item-image"
                                    alt="captcha"
                                >
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div>
            <button
                type="button"
                class="btn btn-primary mt-2"
                @click="onContinueButtonClick"
                :disabled="!isReadyToSubmit"
            >Continuer
            </button>
        </div>
    </template>
    <template v-else-if="isGenericError">
        <div class="alert-container alert-warning" role="alert">
            <p class="alert">
                <span class="alert-icon" aria-hidden="true"></span>
                <span class="ob1-alert-title">Erreur</span>
            </p>
            <div class="alert-content">
                <p class="alert-text">Une erreur technique est survenue... Nous vous invitons à réessayer.</p>
            </div>
        </div>
    </template>
    <template v-else>
        <div></div>
    </template>


</div>
`
});
